import { addNotification, NotificationType } from "@flixbus-phx/marketplace-common";
import * as React from "react";
import { useIntl } from "react-intl";
import ConfirmationPopup from "../../../../shared/components/confirmationPopup/ConfirmationPopup";
import { addRideUpdateErrors, updateRideUpdateErrors } from "../../../../state/rideUpdateErrors/handleRideUpdateErrors";
import { RideUpdate } from "../../../../state/rideUpdateErrors/types";
import { FilterRidesForRideActionsQuery, useRestartSalesMutation } from "../../api/operations.generated";
import getRidesWithFailedUpdate from "../../helpers/getRidesWithFailedUpdate/getRidesWithFailedUpdate";
import { includeAndExcludeRidesForSalesStopAndSalesRestart } from "../../helpers/includeAndExcludeRidesForBatchOperations/includeAndExcludeRidesForBatchOperations";
import BatchAdjustExcludedRides from "../../ui/batchAdjustExcludedRides/BatchAdjustExcludedRides";

export type BatchRestartSalesPopupProps = {
  selectedRides: FilterRidesForRideActionsQuery["filterRides"]["items"];
  onClose: () => void;
};

const BatchRestartSalesPopup: React.FC<BatchRestartSalesPopupProps> = ({ selectedRides, onClose }) => {
  const { formatMessage } = useIntl();

  const [includedRides, excludedRides] = includeAndExcludeRidesForSalesStopAndSalesRestart(
    selectedRides,
    "salesRestartNotPossibleReasons"
  );

  const [restartSales, { loading }] = useRestartSalesMutation({
    onCompleted: (data) => {
      onClose();

      const ridesSuccess = data.restartSales.map((ride) => ride.id);
      const ridesFail = getRidesWithFailedUpdate(includedRides, ridesSuccess);

      updateRideUpdateErrors(ridesSuccess, ridesFail, RideUpdate.RestartSales);

      if (ridesSuccess.length) {
        addNotification({
          message: formatMessage(
            { id: "ride.batchUpdate.notification.restartSales.success" },
            {
              countRides: data.restartSales.length,
            }
          ),
          type: NotificationType.Success,
        });
      }

      if (ridesFail.length) {
        addNotification({
          message: formatMessage(
            { id: "ride.batchUpdate.notification.restartSales.fail" },
            {
              countRides: ridesFail.length,
            }
          ),
          type: NotificationType.Danger,
          isPermanent: true,
        });
      }
    },
    onError: () => {
      onClose();
      addRideUpdateErrors(includedRides, RideUpdate.RestartSales);
      addNotification({
        message: formatMessage(
          { id: "ride.batchUpdate.notification.restartSales.fail" },
          {
            countRides: includedRides.length,
          }
        ),
        type: NotificationType.Danger,
        isPermanent: true,
      });
    },
  });

  const handleRestartSales = () => {
    restartSales({
      variables: {
        rides: includedRides,
      },
    });
  };

  return (
    <ConfirmationPopup
      active
      titleText={formatMessage(
        { id: "ride.batchUpdate.popup.restartSales.title" },
        {
          countRides: includedRides.length,
        }
      )}
      confirmButtonText={formatMessage({ id: "general.yes" })}
      onConfirm={() => {
        handleRestartSales();
      }}
      onCancel={() => onClose()}
      submitLoading={loading}
    >
      {formatMessage({ id: "ride.restartSales.confirmationContent" })}
      {excludedRides.length > 0 && <BatchAdjustExcludedRides rides={excludedRides} type="salesRestart" />}
    </ConfirmationPopup>
  );
};

export default BatchRestartSalesPopup;
